
import React, { lazy, Suspense, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Skeleton } from "@/components/ui/skeleton";
import { preloadCriticalImages } from '@/utils/optimizeImages';
import { Helmet } from 'react-helmet-async';

// Import the hero component directly to ensure it loads immediately
import ServicesHero from '@/components/services/ServicesHero';

// Lazily load components that aren't needed for initial render
const TeachingOverview = lazy(() => import('@/components/services/TeachingOverview'));
const PricingPlans = lazy(() => import('@/components/services/PricingPlans'));
const TeachingApproach = lazy(() => import('@/components/services/TeachingApproach'));
const FAQ = lazy(() => import('@/components/services/FAQ'));
const ServicesCallToAction = lazy(() => import('@/components/services/ServicesCallToAction'));

// Loading fallback components
const ContentSkeleton = () => (
  <div className="py-16">
    <div className="container mx-auto px-4">
      <Skeleton className="h-10 w-1/3 mx-auto mb-6" />
      <Skeleton className="h-4 w-full mb-4 max-w-3xl mx-auto" />
      <Skeleton className="h-4 w-5/6 mb-8 max-w-3xl mx-auto" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <Skeleton className="h-96 w-full rounded-lg" />
        <Skeleton className="h-96 w-full rounded-lg" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    </div>
  </div>
);

const Services = () => {
  // Preload critical images when component mounts
  useEffect(() => {
    // Preload hero image and other critical assets
    preloadCriticalImages([
      '/lovable-uploads/c3099eea-1cdc-462a-9e3c-85f757436352.png',
      '/lovable-uploads/3498deda-3d45-422b-bce5-7a5717ed23da.png'
    ]);
    
    // Add optimization hints for browsers
    if (document.head) {
      const linkPreconnect = document.createElement('link');
      linkPreconnect.rel = 'preconnect';
      linkPreconnect.href = window.location.origin;
      document.head.appendChild(linkPreconnect);
    }
  }, []);
  
  return (
    <Layout>
      <div id="top"></div>
      <Helmet>
        <title>Klaverundervisning i Storkøbenhavn | Priser & Information</title>
        <meta name="description" content="Få professionel klaverundervisning for alle niveauer. Tilbyder enkeltlektioner, sæsonpakker og klippekort. Book en gratis prøvetime i dag!" />
        <link rel="canonical" href="https://pianoundervisning.dk/undervisning" />
      </Helmet>
      
      {/* Hero is loaded directly, not lazy-loaded */}
      <ServicesHero />
      
      <Suspense fallback={<ContentSkeleton />}>
        <TeachingOverview />
      </Suspense>
      <Suspense fallback={<ContentSkeleton />}>
        <PricingPlans />
      </Suspense>
      <Suspense fallback={<ContentSkeleton />}>
        <TeachingApproach />
      </Suspense>
      <Suspense fallback={<ContentSkeleton />}>
        <FAQ />
      </Suspense>
      <Suspense fallback={<ContentSkeleton />}>
        <ServicesCallToAction />
      </Suspense>
    </Layout>
  );
};

export default Services;
